export type CalendarDayCell = {
  dayNum: number;
  isCurrentMonth: boolean;
  dateStr: string;
  isToday: boolean;
};

export const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const getOffsetDateString = (offsetDays: number) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const formatTime12h = (time24: string) => {
  if (!time24) return '';
  const [hourStr, minStr] = time24.split(':');
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  hour = hour ? hour : 12;
  return `${String(hour).padStart(2, '0')}:${minStr} ${ampm}`;
};

export const convert12hTo24h = (h12: number, m: number, period: 'AM' | 'PM'): string => {
  let h24 = h12;
  if (period === 'PM' && h12 !== 12) h24 = h12 + 12;
  else if (period === 'AM' && h12 === 12) h24 = 0;
  return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const convert24hTo12h = (time24: string) => {
  const [hStr, mStr] = time24.split(':');
  const h24 = parseInt(hStr, 10) || 0;
  const m = parseInt(mStr, 10) || 0;
  const period: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
  let h12 = h24 % 12;
  if (h12 === 0) h12 = 12;
  return { h12, m, period };
};

export const hasTimeOverlap = (startA: string, endA: string, startB: string, endB: string) =>
  startA < endB && startB < endA;

export function buildMonthCalendarDays(currentMonth: Date): CalendarDayCell[] {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDayIndex = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const prevTotalDays = new Date(year, month, 0).getDate();

  const days: CalendarDayCell[] = [];
  const todayDate = new Date();
  const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;

  for (let i = firstDayIndex - 1; i >= 0; i--) {
    const d = prevTotalDays - i;
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const dateStr = `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    days.push({ dayNum: d, isCurrentMonth: false, dateStr, isToday: dateStr === todayStr });
  }

  for (let i = 1; i <= totalDays; i++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    days.push({ dayNum: i, isCurrentMonth: true, dateStr, isToday: dateStr === todayStr });
  }

  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    const dateStr = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    days.push({ dayNum: i, isCurrentMonth: false, dateStr, isToday: dateStr === todayStr });
  }

  return days;
}

export function timeToMinutes(time24: string): number {
  const [h, m] = time24.split(':').map((v) => parseInt(v, 10));
  return (h || 0) * 60 + (m || 0);
}

export function getWeekOfMonth(date: Date): number {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  return Math.ceil((date.getDate() + first.getDay()) / 7);
}

/**
 * Adds minutes to a 24h time string (HH:MM) and returns the result.
 * Clamps to 23:59 — does not roll over to next day.
 */
export function addMinutesToTime(time24: string, minutes: number): string {
  const totalMins = timeToMinutes(time24) + minutes;
  const clamped = Math.min(totalMins, 24 * 60); // cap at midnight
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function isSlotInPast(dateStr: string, endTime24: string): boolean {
  if (!dateStr || !endTime24) return false;
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;

  if (dateStr < todayStr) return true;
  if (dateStr > todayStr) return false;

  const [eh, em] = endTime24.split(':').map((n) => parseInt(n, 10));
  const slotEndObj = new Date(now.getFullYear(), now.getMonth(), now.getDate(), eh || 0, em || 0, 0);
  return slotEndObj.getTime() <= now.getTime();
}

export function isSlotStartInPast(dateStr: string, startTime24: string): boolean {
  if (!dateStr || !startTime24) return false;
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const todayStr = `${yyyy}-${mm}-${dd}`;

  if (dateStr < todayStr) return true;
  if (dateStr > todayStr) return false;

  const [sh, sm] = startTime24.split(':').map((n) => parseInt(n, 10));
  const slotStartObj = new Date(now.getFullYear(), now.getMonth(), now.getDate(), sh || 0, sm || 0, 0);
  return slotStartObj.getTime() <= now.getTime();
}

